/* eslint-disable @typescript-eslint/consistent-type-imports */
import {
  Typography,
  useTheme,
  Box,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Grid,
  TextField,
  InputLabel,
  Tooltip,
} from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import CustomDataGrid from 'components/customDataGrid/CustomDataGrid';
import { Fragment, useState, useEffect } from 'react';
import { LoadingButton } from '@mui/lab';

import { colorTheme } from '../../theme';

import { useNavigate } from 'react-router-dom';

import { useSelector, useDispatch } from 'react-redux';
import { type RootState } from 'store';
import { DialogBox, Header } from 'components';
import AddComplaints from './AddComplaints';
import {
  exportComplaints,
  getAllComplaints,
  updateComplaintStatus,
} from 'service/ComplaintService';
import {
  createComplatintForSupport,
  getAllSupportEmployeeList,
} from 'service/OperationalTeamService';
import {
  ComplaintAttributesType,
  SupportTeamEmployeeType,
} from 'types/services';
import {
  setSnackBarFailed,
  setSnackBarInfo,
  setSnackBarSuccess,
} from 'store/snackBar';
import { strings } from 'i18n/Strings';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import {
  complaintStatusTypeOption,
  exportExcelFileNames,
} from 'constants/constantText';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';

type GridRowData = Record<string, unknown>;
export interface ComplaintFilterType {
  startDate?: string | null;
  endDate?: string | null;
  status?: string;
}

const Complaints = (): JSX.Element => {
  const currentTheme = useTheme();
  const navigate = useNavigate();
  const colors = colorTheme(currentTheme.palette.mode);
  const [complaints, setComplaints] = useState<ComplaintAttributesType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [supportEmployeeList, setSupportEmployeeList] = useState<
    SupportTeamEmployeeType[]
  >([]);
  const [pageCount, setPageCount] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const pageInfo = useSelector((state) => state?.pagination?.complaints);
  const { empId } = useSelector((state: RootState) => state.onboarding);

  const [filters, setFilters] = useState<ComplaintFilterType>({
    startDate: null,
    endDate: null,
    status: '',
  });

  const dispatch = useDispatch();
  const userId = empId;

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };

  const handleEvent = (id: number, supportEmpId: number): void => {
    if (empId === supportEmpId) {
      navigate(`/customer-complaints/${id}`);
    } else {
      dispatch(
        setSnackBarInfo({
          snackBarMessage: strings.displayText.onlyYourAssinedCmpl,
        }),
      );
    }
  };

  const getComplaintDetails = (): void => {
    getAllComplaints(
      Number(pageInfo?.page) + 1,
      pageInfo?.pageSize,
      pageInfo?.sortBy,
      pageInfo?.sortOrder,
      searchValue,
      filters,
    )
      .then((res) => {
        if (typeof res !== 'undefined' && 'basicInfo' in res) {
          const { basicInfo } = res as {
            basicInfo: ComplaintAttributesType[];
          };
          if (basicInfo) {
            setComplaints(basicInfo?.rows);
            setPageCount(basicInfo?.count);
          }
        }
      })
      .catch((err: Error) => {
        throw err;
      });
  };
  const getSupportEmployeeList = (): void => {
    getAllSupportEmployeeList()
      .then((res) => {
        if (typeof res !== 'undefined' && 'data' in res) {
          const { data } = res as {
            data: SupportTeamEmployeeType[];
          };
          if (data) {
            setSupportEmployeeList(data);
          }
        }
      })
      .catch((err) => err);
  };

  const handleSelectChange = (event, row): void => {
    const { value } = event.target;
    const data = {
      supportEmployeeId: value,
      createdBy: empId,
      updatedBy: empId,
      complaintId: row.complaintId,
    };
    const newComplaints = complaints.map((complaint) => {
      if (complaint.complaintId === row.complaintId) {
        return {
          ...complaint,
          agentId: Number(value),
        };
      } else {
        return complaint;
      }
    });

    setComplaints(newComplaints);
    void createComplatintForSupport(data);
  };

  const handleExport = async (): Promise<void> => {
    if (!filters.startDate || !filters.endDate) {
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.selectDateRange,
        }),
      );
      return;
    }

    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    const dateDifference =
      (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

    if (dateDifference > 60) {
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.dateExceeded,
        }),
      );
      return;
    }
    try {
      const url = await exportComplaints(filters);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportExcelFileNames.COMPLAINTS;
      link.click();

      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.exportComplaintSuccess,
        }),
      );
    } catch (error) {
      console.log('Export Error', error);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.orderDetailsExportFail,
        }),
      );
    }
  };

  useEffect(() => {
    getSupportEmployeeList();
  }, []);

  const handleApplyFilter = async (): Promise<void> => {
    dispatch(setCurrentPage({ key: 'complaints', value: 0 }));

    getComplaintDetails();
  };

  useEffect(() => {
    getComplaintDetails();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    searchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const handleUpdateStatus = async (
    complaintId: number,
    status: string,
    supportEmpId: number,
  ): Promise<void> => {
    if (empId === supportEmpId) {
      if (complaintId) {
        const data = {
          status,
          userId,
          createdBy: empId,
          createdByType: 'employee',
        };

        await updateComplaintStatus(complaintId, data);
      }
    } else {
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.onlyYourCmplStatus,
        }),
      );
    }

    getComplaintDetails();
  };

  const handleCreateDialogClose = (): void => {
    setIsModalOpen(false);
  };

  const handleFilterChange = (filterType: string, value: string): void => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  const getComplaintStatus = (status: string): string => {
    switch (status) {
      case 'Close':
        return strings.displayText.closedText;
      case 'Re-open':
        return strings.displayText.reOpenText;
      default:
        return status;
    }
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'createdAt',
      width: 150,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Date
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.createdAt)}
        </Typography>
      ),
    },
    {
      field: 'complaintId',
      width: 150,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Complaint ID
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.complaintId}
        </Typography>
      ),
    },
    {
      field: 'user.first_name',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Name
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.firstName} {row?.lastName}
        </Typography>
      ),
    },

    {
      field: 'complaintTitle',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Subject
        </Typography>
      ),
      width: 150,
      renderCell: ({ row }) => (
        <Tooltip title={row?.complaintTitle} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row?.complaintTitle}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'description',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Description
        </Typography>
      ),
      width: 200,
      renderCell: ({ row }) => (
        <Tooltip title={row?.description} placement="top">
          <Typography
            variant="h5"
            color={colors.gray[100]}
            fontSize={14}
            title={row?.description}
          >
            {row?.description?.length > 20
              ? `${(row.description as string).substring(0, 20)}....`
              : row.description}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'assign',
      sortable: false,
      renderHeader: () => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          style={{ textAlign: 'center', marginLeft: 15 }}
        >
          Assign
        </Typography>
      ),
      width: 200,
      renderCell: ({ row }) => {
        const activeValue = supportEmployeeList.find(
          (employee) => Number(employee.empId) === row.agentId,
        );
        return (
          <FormControl fullWidth>
            <Select
              labelId="admin-detail-select-label"
              margin="normal"
              label="Assigned"
              value={activeValue?.empId}
              onChange={(event: unknown) => {
                handleSelectChange(event, row);
              }}
            >
              {supportEmployeeList.map((element, i) => (
                <MenuItem key={i} value={element.empId}>
                  {element.firstName || ''} {element.lastName || ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: 'status',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Status
        </Typography>
      ),
      width: 100,
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {getComplaintStatus(row?.status)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      sortable: false,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Actions
        </Typography>
      ),
      width: 100,
      renderCell: ({ row }) => (
        <Box>
          {row?.status === 'New' ? (
            '-'
          ) : (
            <>
              {row?.status === 'Close' ? (
                <Chip
                  label="Re-open"
                  variant="outlined"
                  color="error"
                  onClick={(event: { stopPropagation: () => void }) => {
                    event.stopPropagation();
                    const reopen = 'Re-open';
                    void handleUpdateStatus(
                      row?.complaintId,
                      reopen,
                      row?.agentId,
                    );
                  }}
                />
              ) : (
                <Chip
                  label="Close"
                  variant="outlined"
                  color="success"
                  onClick={(event: { stopPropagation: () => void }) => {
                    event.stopPropagation();
                    const close = 'Close';
                    void handleUpdateStatus(
                      row?.complaintId,
                      close,
                      row?.agentId,
                    );
                  }}
                />
              )}
            </>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Fragment>
      <Box>
        <Header title="Complaints" />
      </Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={5} />
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.startDate}
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.endDate}
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 200, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Status"
              value={filters.status}
              onChange={(e) => {
                handleFilterChange('status', e.target.value);
              }}
            >
              {complaintStatusTypeOption.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            onClick={() => {
              void handleApplyFilter();
            }}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>
      </Grid>
      <CustomDataGrid
        columns={columns}
        addButtonTitle={'Add Complaints'}
        rows={complaints && (complaints as GridRowData[])}
        onRowClick={(row) => {
          const id: string | number = row?.row?.complaintId;
          handleEvent(id, row?.row?.agentId);
        }}
        onAddButtonClick={() => {
          setIsModalOpen(true);
        }}
        getRowId={(row: GridRowData) =>
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-base-to-string
          `${String(row?.complaintId)}${row.email ? row.email : ''}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        isShowExport
        onExportClick={() => {
          void handleExport();
        }}
        currentPage="complaints"
        pageCount={pageCount}
        searchEnabled={true}
        handleSearch={handleSearch}
        searchPlaceholder={strings.displayText.searchLabel6}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          dispatch(
            setSortBy({
              key: 'complaints',
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: 'complaints',
              value: mode[0].sort,
            }),
          );
          dispatch(setCurrentPage({ key: 'complaints', value: 0 }));
        }}
      />
      <DialogBox
        open={isModalOpen}
        onClose={handleCreateDialogClose}
        title="Create Complaints"
      >
        <AddComplaints
          onClose={handleCreateDialogClose}
          getComplaintDetails={getComplaintDetails}
          supportEmpList={supportEmployeeList}
        />
      </DialogBox>
    </Fragment>
  );
};

export default Complaints;
